import { Component , signal, ChangeDetectorRef, OnInit, Input, SimpleChanges, OnChanges, HostListener, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, DateSelectArg, EventClickArg, EventApi, EventChangeArg, EventSourceInput, EventInput } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { createEventId } from './event-utils';
import { Event, Farm } from '../../../models/types';
import { EventService } from '../../services/event.service';
import { LoginService } from '../../services/login.service';
import Swal from 'sweetalert2';
import { ProfilePageService } from '../../services/profile-page.service';
import { catchError, of, switchMap } from 'rxjs';
import slLocale from '@fullcalendar/core/locales/sl.js'; // Import the Slovenian locale


function transformEvent(event: Event): EventInput {
  const eventApi: EventInput = {
    id: String(event.eventID), 
    title: event.title,
    start: new Date(event.start),
    end: new Date(event.end),
    allDay: event.allDay,
    backgroundColor: event.color,
    extendedProps: {
      description: event.description, // Add description to extendedProps
      imageRef: event.imageRef,        // Assuming this was already present
      organisation: event.organisationID // Assuming this was already present
    }
  };
  return eventApi;
}


@Component({
  selector: 'app-full-calendar',
  standalone: true,
  imports: [FullCalendarModule, CommonModule, RouterOutlet],
  templateUrl: './full-calendar.component.html',
  styleUrls: ['./full-calendar.component.scss']
})
export class FullCalendarComponent implements OnChanges {
  calendarVisible = signal(true);
  calendarOptions = null as any;
  currentEvents = signal<EventApi[]>([]);
  selectedColor = signal<string>('#FF5733');
  @Input() onMarketPage: boolean | undefined = undefined;
  @Input() farmID: number | undefined = undefined;
  @Input() admin: boolean | undefined = undefined;

  interactionsEnabled  = true;

  calendarWidth = 1200;
  calendarTransform = 'translate(-25%, -25%) scale(0.5)';
  @ViewChild('calendar') calendar: any;

  farm: Farm = {
    userID: 0,
    farmID: 0,
    name: '',
    address: '',
    phone: '',
    email: '',
    employees: [],
    description: '',
    products: []
  };
  
  colors: string[] = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#00FFFF',
    '#FF00FF', '#FFA500', '#800080', '#FFC0CB', '#A52A2A', '#000080', '#808000'
  ];

  smallScreen = false;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private eventService: EventService,
    private loginService: LoginService,
    private profilePageService: ProfilePageService) {
  }

  ngOnInit(): void {
    if (window.innerWidth < 800) {
      this.smallScreen = true;
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['onMarketPage'] || changes['admin'] || changes['farmID']) {
      this.loadEvents();
    }
  }

  customNextDay() {
    this.calendar.getApi().next();
  }

  customPrevDay() {
    this.calendar.getApi().prev();
  }

  customToday() {
    this.calendar.getApi().today();
  }

  customMonthView() {
    this.calendar.getApi().changeView('dayGridMonth');
  }

  customWeekView() {
    this.calendar.getApi().changeView('timeGridWeek');
    this.calendarOptions.height = 600;
    this.loadEvents();

  }

  customDayView() {
    this.calendar.getApi().changeView('timeGridDay');
  }

  customListView() {
    this.calendar.getApi().changeView('listWeek');
  }

  @HostListener('window:resize', ['$event'])
    onResize(event: { target: { innerWidth: any; }; }) {
    event.target.innerWidth;
    this.calendarOptions.updateSize();
    this.calendar.
    console.log('Width:', event.target.innerWidth);
    if (event.target.innerWidth < 800 && !this.smallScreen) {
      this.smallScreen = true;
      this.loadEvents();
    } else if (event.target.innerWidth >= 800 && this.smallScreen) {
      this.smallScreen = false;
      this.loadEvents();
    }
  }

  loadEvents(): void {
    if(this.admin){
      console.log('Admin Calendar');
      this.initAdminCalendar();
    } else {
      console.log('User Calendar');
      this.initCalendar();
    }
  }

  initCalendar(): void {
    let fetchFarmInfo$ = of(undefined); // Default to an observable that emits undefined
    if (this.onMarketPage === undefined) {
      fetchFarmInfo$ = this.profilePageService.getFarmInfoForUser(this.farmID!).pipe(
        switchMap(response => {
          this.farm = response.body!;
          this.farmID = this.farm.farmID;
          console.log('FarmID on Full Calendar:', this.farmID);
          return of(undefined); // Emit a value to complete the observable chain
        }),
        catchError(error => {
          console.error('Error getting farm info', error);
          return of(undefined); // Continue the chain despite the error
        })
      );
    }

    fetchFarmInfo$.pipe(
      switchMap(() => {
        const fetchEvents = this.onMarketPage 
          ? this.eventService.getAllEvents()
          : this.eventService.getEventsByFarmId(this.farmID!);
        return fetchEvents;
      })
    ).subscribe((events: any) => {
      const transformedEvents: EventInput[] = [];
      for (const event of events.events) {
        const eventApi = transformEvent(event);
        transformedEvents.push(eventApi);
      }
      console.log('Events', transformedEvents)
      this.interactionsEnabled = !this.onMarketPage! && this.admin!;

      this.calendarOptions = signal<CalendarOptions>({
        plugins: [
          interactionPlugin,
          dayGridPlugin,
          timeGridPlugin,
          listPlugin,
        ],
        headerToolbar: {
          left: this.smallScreen ? '' : 'prev,next today',
          center: 'title',
          right: this.smallScreen ? '' : 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        },
        initialView: 'dayGridMonth',
        locale: slLocale, // Set the Slovenian locale
        initialEvents: transformedEvents,
        weekends: true,
        editable: this.interactionsEnabled,
        selectable: this.interactionsEnabled,
        selectMirror: true,
        dayMaxEvents: true,
        eventResizableFromStart: true,
        select: this.handleDateSelect.bind(this),
        eventClick: this.handleEventClick.bind(this),
        eventsSet: this.handleEventsSet.bind(this),
        eventChange: this.handleEventChange.bind(this),
      });

      console.log('Interactions Enabled:', this.interactionsEnabled);
      this.changeDetector.detectChanges();
    });
  }

  initAdminCalendar(): void {
    let fetchFarmInfo$ = of(undefined); // Default to an observable that emits undefined
    if (this.onMarketPage === undefined) {
      const userID = this.loginService.GetLoggedInUserInfo()!.userID;
      fetchFarmInfo$ = this.profilePageService.getFarmInfo(userID).pipe(
        switchMap(response => {
          this.farm = response.body!;
          this.farmID = this.farm.farmID;
          console.log('FarmID on Full Calendar:', this.farmID);
          return of(undefined); // Emit a value to complete the observable chain
        }),
        catchError(error => {
          console.error('Error getting farm info', error);
          return of(undefined); // Continue the chain despite the error
        })
      );
    }

    fetchFarmInfo$.pipe(
      switchMap(() => {
        const fetchEvents = this.onMarketPage 
          ? this.eventService.getAllEvents()
          : this.eventService.getEventsByFarmId(this.farmID!);
        return fetchEvents;
      })
    ).subscribe((events: any) => {
      const transformedEvents: EventInput[] = [];
      for (const event of events.events) {
        const eventApi = transformEvent(event);
        transformedEvents.push(eventApi);
      }
      console.log('Events', transformedEvents)
      this.interactionsEnabled = !this.onMarketPage! && this.admin!;
      this.calendarOptions = signal<CalendarOptions>({
        plugins: [
          interactionPlugin,
          dayGridPlugin,
          timeGridPlugin,
          listPlugin,
        ],
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        },
        initialView: 'dayGridMonth',
        locale: slLocale, // Set the Slovenian locale
        initialEvents: transformedEvents,
        weekends: true,
        editable: this.interactionsEnabled,
        selectable: this.interactionsEnabled,
        selectMirror: true,
        dayMaxEvents: true,
        eventResizableFromStart: true,
        select: this.handleDateSelect.bind(this),
        eventClick: this.handleEventClick.bind(this),
        eventsSet: this.handleEventsSet.bind(this),
        eventChange: this.handleEventChange.bind(this),
      });

      console.log('Interactions Enabled:', this.interactionsEnabled);
      this.changeDetector.detectChanges();
    });
  }
  
  handleCalendarToggle() {
    this.calendarVisible.update((bool) => !bool);
  }

  handleDateSelect(selectInfo: DateSelectArg) {
    Swal.fire({
      title: 'Vnesite naslov dogodka',
    input: 'text',
    inputPlaceholder: 'Naslov dogodka',
    showCancelButton: true,
    confirmButtonText: 'Shrani',
    cancelButtonText: 'Prekliči',
    inputValidator: (value) => {
        if (!value) {
          return 'Prosim vnesite naslov dogodka';
        }
        return '';
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const title = result.value;
        Swal.fire({
          title: 'Vnesite opis dogodka',
          input: 'textarea',
          inputPlaceholder: 'Opis dogodka',
          showCancelButton: true,
          confirmButtonText: 'Naprej &rarr;',
          cancelButtonText: 'Prekliči',
        }).then((descResult) => {
          if (descResult.isConfirmed) {
            const description = descResult.value;
            const calendarApi = selectInfo.view.calendar;
  
            Swal.fire({
              title: 'Obvesti naročnike?',
              text: 'Ali želite o dogodku obvestiti naročnike? Obvestila bodo prejeli na e-pošto.',
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Da',
              cancelButtonText: 'Ne'
            }).then((notifyResult) => {
              const notifySubscribers = notifyResult.isConfirmed;
  
              const newEvent: Event = {
                eventID: createEventId(),
                title,
                start: selectInfo.startStr,
                end: selectInfo.endStr,
                allDay: selectInfo.allDay,
                farmID: this.farmID!,
                color: this.selectedColor(),
                description, // Add description to new event
                notify: notifySubscribers,
                imageRef: '', // Assuming this was already present
                organisationID: -1
              };
  
              this.eventService.addEvent(newEvent).subscribe(
                addedEvent => {
                  calendarApi.addEvent(transformEvent(addedEvent));
                  console.log('Event added successfully');
                  Swal.fire({
                    title: 'Dogodek dodan!',
                    text: 'Dogodek je bil dodan.',
                    icon: 'success',
                    confirmButtonText: 'Naprej',
                  }).then(() => {
                    window.location.reload();
                  });
                },
                error => {
                  console.error('Error adding event', error);
                  Swal.fire({
                    title: 'Napaka',
                    text: 'Pri dodajanju dogodka je prišlo do napake.',
                    icon: 'error',
                    confirmButtonText: 'Naprej',
                  });
                }
              );
  
            });
          }
        });
      }
    });
  }

  handleDivClick() {
    console.log('Div clicked');
    this.profilePageService.openAllEventsModal(this.currentEvents());
  }

  handleEventClick(clickInfo: EventClickArg) {
  if (this.onMarketPage) {
    const event = clickInfo.event;
    const title = event.title;
    const start = event.startStr;
    const end = event.endStr;
    const description = event.extendedProps['description']; // Access description

    Swal.fire({
      title: `Podatki o dogodku:`,
      html: `<strong>Naslov:</strong> ${title}<br><strong>Začetek:</strong> ${start}<br><strong>Konec:</strong> ${end}<br><strong>Opis:</strong> ${description ? description : 'Ni opisa...'}<br>`,
      icon: 'info',
      confirmButtonText: 'Zapri'
    });
  } else {
    Swal.fire({
      title: 'Ste prepričani?',
      text: `Ali res želite izbrisati dogodek '${clickInfo.event.title}'?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Da, izbriši',
      cancelButtonText: 'Ne, prekliči',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.eventService.deleteEvent(clickInfo.event.id).subscribe(() => {
          clickInfo.event.remove();
          Swal.fire({
            title: 'Izbrisano!',
            text: 'Dogodek je bil uspešno izbrisan.',
            icon: 'success'
          });
        }, error => {
          Swal.fire({
            title: 'Napaka!',
            text: 'Pri izbrisu dogodka je prišlo do napake.',
            icon: 'error'
          });
        });
      }
    });
  }
}


  handleEventsSet(events: EventApi[]) {
    this.currentEvents.set(events);
    this.changeDetector.detectChanges(); // workaround for pressionChangedAfterItHasBeenCheckedError
  }
  
  handleEventChange(changeInfo: EventChangeArg) {
    const updatedEvent: Event = {
      eventID: changeInfo.event.id,
      title: changeInfo.event.title,
      start: changeInfo.event.startStr,
      end: changeInfo.event.endStr,
      allDay: changeInfo.event.allDay,
      farmID: this.farmID!,
      color: changeInfo.event.backgroundColor,
      description: changeInfo.event.extendedProps['description'], // Add description
      notify: changeInfo.event.extendedProps['notify'],
      imageRef: changeInfo.event.extendedProps['imageRef'], // Assuming this was already present
      organisationID: changeInfo.event.extendedProps['farm']
    };
    this.eventService.updateEvent(updatedEvent).subscribe(() => {
      console.log('Event updated successfully');
    });
  }
}
